import { useState, useEffect, useCallback, useRef } from "react";
import {
  BUILD_HASH,
  BUILD_VERSION_NUMBER,
} from "@/generated/buildVersion";

const POLL_INTERVAL_MS = 60_000; // 60 seconds
const DISMISSED_KEY = "version-check-dismissed";

interface LatestVersionData {
  hash: string;
  version_number: number;
}

interface VersionHistoryEntry {
  version_number: number;
  hash: string;
  released_at: string;
}

export interface VersionDetail {
  version_number: number;
  hash: string;
  released_at: string;
  description: string[];
}

interface UseVersionCheckResult {
  isOutdated: boolean;
  missedVersions: VersionDetail[];
  dismiss: () => void;
}

function isLocalhost(): boolean {
  if (typeof window === "undefined") return true;
  return ["localhost", "127.0.0.1", "::1"].includes(window.location.hostname);
}

function getDismissedHash(): string | null {
  try {
    return localStorage.getItem(DISMISSED_KEY);
  } catch {
    return null;
  }
}

function setDismissedHash(hash: string): void {
  try {
    localStorage.setItem(DISMISSED_KEY, hash);
  } catch {
    // localStorage unavailable — ignore
  }
}

export function useVersionCheck(): UseVersionCheckResult {
  const [isOutdated, setIsOutdated] = useState(false);
  const [missedVersions, setMissedVersions] = useState<VersionDetail[]>([]);
  const remoteHashRef = useRef<string | null>(null);
  const isFetchingDetailsRef = useRef(false);

  const fetchMissedVersionDetails = useCallback(
    async (history: VersionHistoryEntry[]) => {
      // Find versions newer than our build
      const missed = history.filter(
        (entry) => entry.version_number > BUILD_VERSION_NUMBER
      );

      if (missed.length === 0) return;

      // Fetch individual version files for each missed version
      const details: VersionDetail[] = [];

      for (const entry of missed) {
        try {
          const resp = await fetch(`/versions/${entry.hash}.json`, {
            cache: "no-store",
          });
          if (resp.ok) {
            const data: VersionDetail = await resp.json();
            details.push(data);
          } else {
            // If individual file not found, use what we have from history
            details.push({
              version_number: entry.version_number,
              hash: entry.hash,
              released_at: entry.released_at,
              description: [],
            });
          }
        } catch {
          details.push({
            version_number: entry.version_number,
            hash: entry.hash,
            released_at: entry.released_at,
            description: [],
          });
        }
      }

      // Sort descending by released_at
      details.sort(
        (a, b) =>
          new Date(b.released_at).getTime() -
          new Date(a.released_at).getTime()
      );

      setMissedVersions(details);
    },
    []
  );

  const checkVersion = useCallback(async () => {
    try {
      const response = await fetch("/latest-version.json", {
        cache: "no-store",
      });
      if (!response.ok) return;

      const data: LatestVersionData = await response.json();
      remoteHashRef.current = data.hash;

      if (data.hash !== BUILD_HASH) {
        // Check if the user already dismissed this specific version
        const dismissed = getDismissedHash();
        if (dismissed === data.hash) {
          setIsOutdated(false);
        } else {
          setIsOutdated(true);

          // Fetch version history to determine missed versions (only once per detection)
          if (!isFetchingDetailsRef.current) {
            isFetchingDetailsRef.current = true;
            try {
              const historyResp = await fetch("/version-history.json", {
                cache: "no-store",
              });
              if (historyResp.ok) {
                const history: VersionHistoryEntry[] =
                  await historyResp.json();
                await fetchMissedVersionDetails(history);
              }
            } catch {
              // Silently ignore — version history not available
            } finally {
              isFetchingDetailsRef.current = false;
            }
          }
        }
      } else {
        setIsOutdated(false);
        setMissedVersions([]);
        isFetchingDetailsRef.current = false;
      }
    } catch {
      // Network error or parse error — silently ignore, no false positives
    }
  }, [fetchMissedVersionDetails]);

  const dismiss = useCallback(() => {
    const hash = remoteHashRef.current;
    if (hash) {
      setDismissedHash(hash);
    }
    setIsOutdated(false);
  }, []);

  useEffect(() => {
    // Skip polling entirely on localhost
    if (isLocalhost()) return;

    // Initial check
    checkVersion();

    // Set up interval
    const intervalId = setInterval(checkVersion, POLL_INTERVAL_MS);

    // Pause/resume on visibility change
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkVersion();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [checkVersion]);

  return { isOutdated, missedVersions, dismiss };
}
